/**
 * CorporateGRIDashboard
 *
 * Tenant-wide rollup of GRI disclosures. View toggle: merged vs per_site.
 * Shows worst-status per disclosure (worst-wins) and the rolled-up workspace
 * counts (complete / partial / missing).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, RefreshCw, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import CorporatePeriodSelector from './CorporatePeriodSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  complete: { icon: CheckCircle, color: 'text-green-600 bg-green-50' },
  partial: { icon: AlertCircle, color: 'text-amber-600 bg-amber-50' },
  missing: { icon: XCircle, color: 'text-red-600 bg-red-50' },
};

const CorporateGRIDashboard = () => {
  const [periodId, setPeriodId] = useState(null);
  const [view, setView] = useState('merged');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [data, setData] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, dataRes] = await Promise.all([
        axios.get(`${API}/api/gri/corporate/dashboard?period_id=${periodId}`, { headers }),
        axios.get(`${API}/api/gri/corporate/data?period_id=${periodId}&view=${view}`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setData(dataRes.data);
    } catch (err) {
      console.error('Corporate GRI fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [periodId, view]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-indigo-600" />
          <h2 className="text-2xl font-bold text-gray-900">Corporate GRI Rollup</h2>
        </div>
        <div className="flex items-center gap-3">
          <CorporatePeriodSelector value={periodId} onChange={setPeriodId} />
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('merged')}
              className={`px-3 py-2 text-sm ${view === 'merged' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Merged
            </button>
            <button
              onClick={() => setView('per_site')}
              className={`px-3 py-2 text-sm ${view === 'per_site' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Per Site
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Studies" value={dashboard.study_count} />
          <StatCard label="Complete" value={dashboard.status?.complete_count ?? 0} tone="green" />
          <StatCard label="Partial" value={dashboard.status?.partial_count ?? 0} tone="amber" />
          <StatCard label="Missing" value={dashboard.status?.missing_count ?? 0} tone="red" />
          <StatCard
            label="Coverage"
            value={`${dashboard.status?.coverage_percentage?.toFixed?.(1) ?? '0'}%`}
          />
        </div>
      )}

      {!loading && data && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Disclosures ({data.disclosures?.length || 0})
          </h3>
          {(!data.disclosures || data.disclosures.length === 0) ? (
            <p className="text-sm text-gray-500">No GRI data found for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2">Disclosure</th>
                    <th className="text-left px-3 py-2">Standard</th>
                    <th className="text-left px-3 py-2">Title</th>
                    <th className="text-left px-3 py-2">Status (worst-wins)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.disclosures.map((d, idx) => {
                    const badge = STATUS_BADGE[d.rolled_up_status] || STATUS_BADGE.missing;
                    const Icon = badge.icon;
                    return (
                      <tr key={`${d.disclosure_number}-${idx}`} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-700">{d.disclosure_number}</td>
                        <td className="px-3 py-2 text-gray-600">{d.gri_standard || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{d.title || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
                            <Icon className="w-3 h-3" />
                            {d.rolled_up_status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, tone = 'gray' }) => {
  const tones = {
    gray: 'text-gray-900',
    green: 'text-green-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tones[tone] || tones.gray}`}>{value}</div>
    </div>
  );
};

export default CorporateGRIDashboard;
